/**
 * Composant Information du joueur
 */

import React from 'react';
import type { Player } from '@pathblock/core';

interface PlayerInfoProps {
  player: Player;
  isCurrentPlayer: boolean;
  isMe: boolean;
  remainingTime: number | null;
  turnTimeLimit: number | null;
  distanceToGoal: number | null;
}

export const PlayerInfo: React.FC<PlayerInfoProps> = ({
  player,
  isCurrentPlayer,
  isMe,
  remainingTime,
  turnTimeLimit,
  distanceToGoal,
}) => {
  const colorClass = player.id === 1 ? 'border-player1' : 'border-player2';
  const bgColor = player.id === 1 ? 'bg-player1/20' : 'bg-player2/20';
  const textColor = player.id === 1 ? 'text-player1' : 'text-player2';

  return (
    <div
      className={`
        card border-2 ${colorClass} ${isCurrentPlayer ? bgColor : ''}
        transition-all duration-300
        ${isCurrentPlayer ? 'scale-105 shadow-xl' : 'opacity-75'}
      `}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={`w-4 h-4 rounded-full ${
              player.id === 1 ? 'bg-player1' : 'bg-player2'
            }`}
          />
          <div className="flex flex-col">
            <span className={`font-bold ${textColor}`}>
              {player.name || `Joueur ${player.id}`}
            </span>
            {isMe && <span className="text-gray-400 text-xs">C'est vous</span>}
          </div>
        </div>
        {isCurrentPlayer && (
          <span className="text-xs bg-white/10 px-2 py-1 rounded-full animate-pulse">
            En jeu
          </span>
        )}
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-400">Murs:</span>
          <div className="flex gap-1">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-4 rounded-sm ${
                  i < player.wallsRemaining ? 'bg-wall' : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
        <span className="text-gray-300 font-mono">
          {player.wallsRemaining}/10
        </span>
      </div>

      {distanceToGoal !== null && (
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-gray-500">Distance au but:</span>
          <span className={`font-mono font-bold ${
            distanceToGoal <= 2 ? 'text-red-400' : distanceToGoal <= 4 ? 'text-yellow-400' : 'text-gray-400'
          }`}>
            {distanceToGoal} coup{distanceToGoal > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {remainingTime !== null && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Temps:</span>
            {isCurrentPlayer ? (
              <span
                className={`font-mono text-lg font-bold ${
                  remainingTime <= 10 ? 'text-red-500 animate-pulse' : 'text-white'
                }`}
              >
                {remainingTime}s
              </span>
            ) : (
              <span className="font-mono text-sm text-gray-500">
                {remainingTime}s
              </span>
            )}
          </div>
          <div className="mt-2 h-1 bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${
                !isCurrentPlayer
                  ? 'bg-gray-600'
                  : remainingTime <= 10
                  ? 'bg-red-500'
                  : 'bg-green-500'
              }`}
              style={{
                width: `${turnTimeLimit ? (remainingTime / turnTimeLimit) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
